import * as v from 'valibot'

import { SOURCE_FORMATS, slackWebhookUrlSchema } from '../MonitorAgent/types'
import { cronExpressionSchema } from '../scheduling'

/** Formats a seed source may carry — the monitor formats plus `unknown`. */
export const SEED_FORMATS = [...SOURCE_FORMATS, 'unknown'] as const

/**
 * One source from the WFL-6846 seed inventory. Lenient (`v.object` strips
 * unknown keys) so the full `seed.json` — provenance, summary, groups, and the
 * extra per-source fields — can be posted verbatim; only what provisioning
 * needs is validated.
 */
export const SeedSourceInputSchema = v.object({
  id: v.pipe(v.string(), v.minLength(1)),
  name: v.pipe(v.string(), v.minLength(1)),
  /** Document or page URL tracked for this source (`direct` fetch target). */
  url: v.pipe(v.string(), v.url()),
  /** Landing page the monitor starts from (`discover` target). */
  startUrl: v.pipe(v.string(), v.url()),
  domain: v.pipe(v.string(), v.minLength(1)),
  expectedFormat: v.picklist(SEED_FORMATS),
  directive: v.picklist(['direct', 'discover']),
  instructions: v.optional(v.string()),
  /**
   * Plain-language curation steps applied to a fresh candidate's fetched
   * bytes before comparison/persistence (WFL-7602) — see
   * `MonitorAgent/types.ts#editInstructions`.
   */
  editInstructions: v.optional(v.string()),
  /**
   * Rialtic reference document this source backs, if already linked. When
   * present, provisioning seeds the monitor's baseline from that record
   * (our own stored copy) instead of a live fetch (WFL-7529).
   */
  referenceDocumentId: v.optional(v.pipe(v.string(), v.minLength(1))),
})

export type SeedSourceInput = v.InferOutput<typeof SeedSourceInputSchema>

export const SeedInputSchema = v.object({
  sources: v.pipe(v.array(SeedSourceInputSchema), v.minLength(1)),
})

export type SeedInput = v.InferOutput<typeof SeedInputSchema>

/** Well-known DO name for the single manager instance every monitor registers with. */
export const MANAGER_NAME = 'registry'

/** A monitor self-registers this when configured; the manager stamps the timestamps. */
export const RegisterInputSchema = v.object({
  id: v.pipe(v.string(), v.minLength(1)),
  type: v.picklist(['source', 'report']),
  name: v.pipe(v.string(), v.minLength(1)),
  domain: v.nullable(v.string()),
  directive: v.nullable(v.picklist(['direct', 'discover'])),
  referenceDocumentId: v.optional(
    v.nullable(v.pipe(v.string(), v.minLength(1))),
    null,
  ),
  discoveryInstructions: v.optional(v.nullable(v.string()), null),
  schedule: cronExpressionSchema,
  bufferWindowDays: v.pipe(
    v.number(),
    v.integer('bufferWindowDays must be an integer'),
    v.minValue(0, 'bufferWindowDays must be >= 0'),
  ),
})

export type RegisterInput = v.InferOutput<typeof RegisterInputSchema>

export interface RegistryEntry
  extends Omit<RegisterInput, 'referenceDocumentId' | 'discoveryInstructions'> {
  /** Always present in stored registry entries; `null` for report monitors. */
  referenceDocumentId: string | null
  /** Original source discovery instructions; `null` for report monitors. */
  discoveryInstructions: string | null
  provisionedAt: string
  updatedAt: string
}

/** Global digest delivery config for the manager. */
export const ManagerConfigSchema = v.object({
  slackWebhookUrl: v.optional(slackWebhookUrlSchema),
  slackChannelId: v.optional(v.string()),
  /** Cron for the batched digest; when set, schedules `sendDigest`. */
  digestSchedule: v.optional(cronExpressionSchema),
})

export type ManagerConfig = v.InferOutput<typeof ManagerConfigSchema>

export interface ManagerState {
  monitors: Record<string, RegistryEntry>
  config: ManagerConfig | null
  schemaVersion: number
}

export const INITIAL_MANAGER_STATE: ManagerState = {
  monitors: {},
  config: null,
  schemaVersion: 1,
}
